import { Receipt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { formatCurrencyMYR } from "@/lib/format";

export const FEES = [
  { teams: 1, amount: 15_000 },
  { teams: 2, amount: 28_000 },
  { teams: 3, amount: 42_000 },
  { teams: 4, amount: 54_000 },
  { teams: 5, amount: 60_000 },
  { teams: 6, amount: 66_000 },
  { teams: 7, amount: 72_000 },
  { teams: 8, amount: 78_000 },
  { teams: 9, amount: 84_000 },
  { teams: 10, amount: 90_000 }
];

export function getParticipationFee(teamCount: number | undefined): number | null {
  if (!teamCount) return null;
  const row = FEES.find((r) => r.teams === teamCount);
  return row ? row.amount : null;
}

type Props = {
  selectedTeamCount?: number;
};

export function ParticipationFeesDialog({ selectedTeamCount }: Props) {
  const { t, i18n } = useTranslation("dashboard");
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="link" size="sm" className="px-0 h-auto">
          <Receipt className="h-3.5 w-3.5" /> {t("fees.viewFees")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("fees.title")}</DialogTitle>
          <DialogDescription>
            {t("fees.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
              <tr>
                <th className="text-left py-2 pr-4">{t("fees.teamsHeader")}</th>
                <th className="text-right py-2">{t("fees.contributionHeader")}</th>
              </tr>
            </thead>
            <tbody>
              {FEES.map((row) => {
                const isSelected = row.teams === selectedTeamCount;
                const teamLabel =
                  row.teams === 1
                    ? t("fees.oneTeam")
                    : t("fees.manyTeams", { word: t(`fees.teamWord.${row.teams}`) });
                return (
                  <tr
                    key={row.teams}
                    className={
                      isSelected
                        ? "border-b last:border-b-0 bg-primary/10 text-primary font-medium"
                        : "border-b last:border-b-0"
                    }
                  >
                    <td className="py-2 pr-4">
                      {teamLabel} ({row.teams})
                    </td>
                    <td className="py-2 text-right font-mono">
                      {formatCurrencyMYR(row.amount, i18n.language)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
